/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Backdrop, Box, CircularProgress } from '@mui/material';
import React from 'react';
import type { FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import {
  defaultFormOptions,
  ReactHookFormProvider,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import {
  AlertBanner,
  ConfirmationServiceProvider,
  CustomDialog,
  dateUtils,
  useConfirmationDialog,
  usePreventBrowserClose,
} from '@opengeoweb/shared';
import { AirmetForm, SigmetForm } from '.';
import { CancelSigmetVolcanicMovements } from './SigmetForm/SigmetCancelVolcanicMovements';
import {
  createAirmetPostParameter,
  createSigmetPostParameter,
  getConfirmationDialogButtonLabel,
  getConfirmationDialogCancelLabel,
  getConfirmationDialogContent,
  getConfirmationDialogTitle,
  getDialogtitle,
  getLifecycleActions,
} from './utils';
import { usePostAirmet, usePostSigmet } from '../../api/hooks';
import { useShortTestHelpers } from '../../hooks/useShortTestHelpers';
import type {
  Airmet,
  AirmetFromFrontend,
  AviationPhenomenaCode,
  AviationProduct,
  CancelAirmet,
  CancelSigmet,
  FormMode,
  ProductConfig,
  ProductStatus,
  ProductType,
  Sigmet,
  SigmetFromFrontend,
} from '../../types';
import {
  isInstanceOfCancelAirmet,
  isInstanceOfCancelSigmet,
  isInstanceOfCancelSigmetOrAirmet,
  ProductAction,
} from '../../types';
import { useSigmetAirmetTranslation } from '../../utils/i18n';

export const isVAPhenomenon = (
  product: AviationProduct,
  action: ProductAction,
): boolean => {
  return (
    action === ProductAction.CANCEL &&
    product !== null &&
    !isInstanceOfCancelSigmetOrAirmet(product) &&
    product.phenomenon === ('VA_CLD' as AviationPhenomenaCode)
  );
};

export const prepareCancelSigmet = (
  sigmet: Sigmet,
  movement: string,
): Sigmet => {
  // If movement UNKNOWN, don't send to BE
  if (movement === 'UNKNOWN') {
    return sigmet;
  }
  return {
    ...sigmet,
    vaSigmetMoveToFIR: movement,
  };
};

export interface ProductFormDialogContentProps {
  isOpen: boolean;
  toggleDialogStatus: (shouldRefreshList?: boolean) => void;
  onRenewClick: () => void;
  mode: FormMode;
  productType: ProductType;
  productConfig: ProductConfig;
  productCanBe: string[];
  product: AviationProduct;
}

const ProductFormDialogContent: React.FC<ProductFormDialogContentProps> = ({
  isOpen,
  toggleDialogStatus,
  onRenewClick,
  mode,
  productType,
  productConfig,
  productCanBe,
  product,
}: ProductFormDialogContentProps) => {
  const { t } = useSigmetAirmetTranslation();
  const postSigmetMutation = usePostSigmet();
  const postAirmetMutation = usePostAirmet();

  const apiCall =
    productType === 'sigmet'
      ? postSigmetMutation.mutateAsync
      : postAirmetMutation.mutateAsync;

  const isLoading =
    productType === 'sigmet'
      ? postSigmetMutation.isPending
      : postAirmetMutation.isPending;

  const { handleSubmit, formState, watch, trigger, getValues } =
    useFormContext();
  const { dirtyFields } = formState;
  const isDirty = Object.keys(dirtyFields).length > 0;
  const { toggleIsDraft, DraftFieldHelper } = useDraftFormHelpers();
  const { isShortTest } = useShortTestHelpers();
  usePreventBrowserClose(isDirty);

  const confirmationDialog = useConfirmationDialog();

  const [errorStoreMessage, setErrorStoreMessage] = React.useState(false);
  const [errorStoreMessageText, setErrorStoreMessageText] = React.useState('');
  const [dialogActionsDisabled, setDialogActionsDisabled] =
    React.useState(false);

  const postProduct = (
    productToPost: FieldValues,
    newStatus: ProductStatus,
  ): void => {
    const uuid = product?.uuid ? product.uuid : null;
    const postProductParam =
      productType === 'sigmet'
        ? createSigmetPostParameter(
            productToPost as Sigmet | CancelSigmet,
            newStatus,
            uuid!,
          )
        : createAirmetPostParameter(
            productToPost as Airmet | CancelAirmet,
            newStatus,
            uuid!,
          );

    apiCall(postProductParam as SigmetFromFrontend & AirmetFromFrontend)
      .then(() => {
        onToggleDialog();
      })
      .catch((error: Error) => {
        setErrorStoreMessage(true);
        setErrorStoreMessageText(error.message || 'Unknown error');
      });
  };

  const onToggleDialog = (shouldRefreshList = true): void => {
    toggleDialogStatus(shouldRefreshList);
  };

  const showConfirmDialog = async (action: ProductAction): Promise<void> =>
    confirmationDialog({
      title: getConfirmationDialogTitle(t, action, productType),
      description: getConfirmationDialogContent(t, action, productType),
      confirmLabel: getConfirmationDialogButtonLabel(t, action, productType),
      cancelLabel: getConfirmationDialogCancelLabel(t, action),
      catchOnCancel: action === ProductAction.CLOSE,
      content:
        isVAPhenomenon(product, action) && !isShortTest() ? (
          <CancelSigmetVolcanicMovements
            productConfig={productConfig}
            sigmet={product as Sigmet}
          />
        ) : null,
    });

  const onProductButtonPress = async (action: ProductAction): Promise<void> => {
    switch (action) {
      case ProductAction.CLOSE:
        if (mode === 'view' || !isDirty) {
          onToggleDialog(false);
        } else {
          toggleIsDraft(true);
          showConfirmDialog(action)
            .then(() => {
              void handleSubmit((formValues) => {
                postProduct(formValues, 'DRAFT');
              })();
            })
            .catch((reason) => {
              if (reason === 'CANCELLED') {
                // close without save
                onToggleDialog(false);
              }
            });
        }
        break;
      case ProductAction.CANCEL: {
        // Check if the product has expired (validDateEnd is in the past)
        const validDateEnd = product?.validDateEnd
          ? dateUtils.utc(product.validDateEnd)
          : null;
        if (validDateEnd && dateUtils.isBefore(validDateEnd, dateUtils.utc())) {
          setErrorStoreMessage(true);
          setErrorStoreMessageText(
            t('cancel-expired-product', { product: productType }),
          );
          break;
        }
        void showConfirmDialog(action).then(() => {
          // Re-check right before submitting in case the dialog stayed open past expiry.
          const latestValidDateEnd = product?.validDateEnd
            ? dateUtils.utc(product.validDateEnd)
            : null;
          if (
            latestValidDateEnd &&
            dateUtils.isBefore(latestValidDateEnd, dateUtils.utc())
          ) {
            setErrorStoreMessage(true);
            setErrorStoreMessageText(
              t('cancel-expired-product', { product: productType }),
            );
            return;
          }

          // if volcanic sigmet - add to which FIR it's moving
          if (isVAPhenomenon(product, action)) {
            postProduct(
              prepareCancelSigmet(
                product as Sigmet,
                watch('vaSigmetMoveToFIR'),
              ),
              'CANCELLED',
            );
          } else {
            postProduct(product, 'CANCELLED');
          }
        });
        break;
      }

      case ProductAction.DISCARD:
        void showConfirmDialog(action).then(() => {
          // If we're discarding a new SIGMET/AIRMET - just close the dialog
          if (mode === 'new' || !product?.uuid) {
            onToggleDialog(false);
          } else {
            // If we're discarding an existing SIGMET/AIRMET - make call to BE
            postProduct(product, 'DISCARDED');
          }
        });
        break;
      case ProductAction.RENEW:
        onRenewClick();
        break;
      default: {
        const isDraft = action === ProductAction.SAVE;
        toggleIsDraft(isDraft);

        if (isDraft) {
          // Run validation manually to display messages
          await trigger();
          const formValues = getValues();

          // Save draft without blocking on validation
          postProduct(formValues, 'DRAFT');
        } else {
          // Validate and submit normally (will block if invalid)
          void handleSubmit((formValues) => {
            void showConfirmDialog(action).then(() => {
              postProduct(formValues, 'PUBLISHED');
            });
          })();
        }
        break;
      }
    }
  };

  const title = getDialogtitle(t, product, productType, productConfig);

  return (
    <CustomDialog
      data-testid="productform-dialog"
      disableEnforceFocus
      fullWidth
      headerActions={getLifecycleActions({
        canBe: productCanBe,
        onButtonPress: onProductButtonPress,
        productType,
        areActionsDisabled: dialogActionsDisabled,
        t,
      })}
      maxWidth="xl"
      onClose={(event, reason): void => {
        if (reason !== 'escapeKeyDown') {
          void onProductButtonPress(ProductAction.CLOSE);
        }
      }}
      open={isOpen}
      style={{ zIndex: 1000 }}
      sx={{
        height: '100%',
        '.MuiDialogContent-root': {
          padding: 0,
        },
      }}
      title={title}
    >
      {errorStoreMessage ? (
        <AlertBanner
          info={errorStoreMessageText}
          severity="error"
          title="An error has occurred while saving, please try again"
        />
      ) : null}
      <Box sx={{ display: 'flex', minHeight: 200, height: '100%' }}>
        {productType === 'sigmet' ? (
          <SigmetForm
            initialCancelSigmet={
              product !== null && isInstanceOfCancelSigmet(product)
                ? product
                : null!
            }
            initialSigmet={
              product !== null && !isInstanceOfCancelSigmet(product)
                ? (product as Sigmet)
                : null!
            }
            isCancelSigmet={
              product !== null && isInstanceOfCancelSigmet(product)
            }
            mode={mode}
            product={product}
            productConfig={productConfig}
            setDialogActionsDisabled={setDialogActionsDisabled}
          />
        ) : (
          <AirmetForm
            initialAirmet={
              product !== null && !isInstanceOfCancelAirmet(product)
                ? (product as Airmet)
                : null!
            }
            initialCancelAirmet={
              product !== null && isInstanceOfCancelAirmet(product)
                ? product
                : null!
            }
            isCancelAirmet={
              product !== null && isInstanceOfCancelAirmet(product)
            }
            mode={mode}
            product={product}
            productConfig={productConfig}
            setDialogActionsDisabled={setDialogActionsDisabled}
          />
        )}
      </Box>
      <DraftFieldHelper />

      <Backdrop
        data-testid="loader"
        open={isLoading}
        sx={{
          zIndex: 1500,
          color: '#fff',
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </CustomDialog>
  );
};

const Wrapper: React.FC<ProductFormDialogContentProps> = ({
  ...props
}: ProductFormDialogContentProps) => {
  return (
    <ReactHookFormProvider
      options={{
        ...defaultFormOptions,
        defaultValues: props.product,
        values: props.product,
      }}
    >
      <ConfirmationServiceProvider>
        <ProductFormDialogContent {...props} />
      </ConfirmationServiceProvider>
    </ReactHookFormProvider>
  );
};

export { Wrapper as ProductFormDialogContent };

export default Wrapper;
